import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { CampaignRecipient } from "@/types/sms";

/** A batch recipient plus the customer's name, for the batch-detail list. */
export type InviteBatchRecipient = CampaignRecipient & { display_name: string | null };

const NAME_LOOKUP_CHUNK = 200;

/**
 * One invite batch's per-customer rows (campaign_recipients for a single
 * `play='invite'` campaign): who was texted, who was skipped and why, with each
 * customer's name from v_customer_marketing. Only fetches when a campaignId is
 * provided (the batch-detail dialog is open). RLS scopes both reads to the
 * caller's store; read-only.
 */
export const useInviteBatchDetail = (campaignId: string | null) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<InviteBatchRecipient[]>({
    queryKey: ["sms-invite-batch-detail", campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("campaign_recipients")
        .select("*")
        .eq("campaign_id", campaignId)
        .order("status", { ascending: true })
        .order("customer_phone", { ascending: true });
      if (error) throw error;
      const recipients = (data ?? []) as CampaignRecipient[];

      const names = new Map<string, string | null>();
      const phones = [...new Set(recipients.map((r) => r.customer_phone))];
      for (let i = 0; i < phones.length; i += NAME_LOOKUP_CHUNK) {
        const { data: rows, error: nameError } = await sb
          .from("v_customer_marketing")
          .select("customer_phone, display_name")
          .in("customer_phone", phones.slice(i, i + NAME_LOOKUP_CHUNK));
        if (nameError) throw nameError;
        for (const row of (rows ?? []) as { customer_phone: string; display_name: string | null }[]) {
          names.set(row.customer_phone, row.display_name);
        }
      }
      return recipients.map((r) => ({ ...r, display_name: names.get(r.customer_phone) ?? null }));
    },
    staleTime: 60 * 1000,
  });
};
